import React, { useState, useEffect } from 'react';
import pb from '../api/pocketbase';
import { exportToExcelCharges, exportToPDF, exportToSVG } from './exportation/exportUtils';
import ExportModal from './exportation/exportModal';

export default function EtatFiltrage({
  filtreDateDebut,
  setFiltreDateDebut,
  filtreDateFin,
  setFiltreDateFin
}) {
  const [showResults, setShowResults] = useState(false);
  const [depensesFiltrees, setDepensesFiltrees] = useState([]);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allDepenses, setAllDepenses] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedProjectData, setSelectedProjectData] = useState(null);

  // Charger toutes les dépenses validées DG au montage du composant
  useEffect(() => {
    const chargerDepensesValideesDG = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await pb.collection('depensesValideesDG').getFullList({
          sort: '-created',
        });
        console.log('Dépenses chargées:', data);
        setAllDepenses(data);
      } catch (error) {
        console.error('Erreur lors du chargement des dépenses validées DG:', error);
        setAllDepenses([]);
      }
      setLoading(false);
    };

    chargerDepensesValideesDG();
  }, []);

  // Filtrage uniquement quand les deux dates sont renseignées
  useEffect(() => {
    if (!filtreDateDebut || !filtreDateFin) {
      setDepensesFiltrees([]);
      return;
    }

    let filtered = allDepenses;
    
    // Filtrer par date de début
    filtered = filtered.filter(dep => {
      if (!dep.dateValidation && !dep.dateValidationDG && !dep.created) return false;
      
      let date;
      const dateField = dep.dateValidation || dep.dateValidationDG || dep.created;
      
      if (dateField && dateField.seconds) {
        date = new Date(dateField.seconds * 1000);
      } else if (dateField) {
        date = new Date(dateField);
      } else {
        return false;
      }
      
      return date >= new Date(filtreDateDebut);
    });
    
    // Filtrer par date de fin
    filtered = filtered.filter(dep => {
      if (!dep.dateValidation && !dep.dateValidationDG && !dep.created) return false;
      
      let date;
      const dateField = dep.dateValidation || dep.dateValidationDG || dep.created;
      
      if (dateField && dateField.seconds) {
        date = new Date(dateField.seconds * 1000);
      } else if (dateField) {
        date = new Date(dateField);
      } else {
        return false;
      }
      
      return date <= new Date(filtreDateFin + "T23:59:59");
    });
    
    setDepensesFiltrees(filtered);
  }, [allDepenses, filtreDateDebut, filtreDateFin]);

  const calculateTotal = () => {
    return depensesFiltrees.reduce((sum, dep) => sum + Number(dep.montant || 0), 0);
  };

  const groupByChantierRubrique = (depenses = []) => {
    const result = {};
    depenses.forEach(depense => {
      const chef = depense.chefChantier || "Inconnu";
      let rubrique = "-";
      if (typeof depense.rubriques === "string") rubrique = depense.rubriques;
      else if (Array.isArray(depense.rubriques)) rubrique = depense.rubriques.join(", ");
      else if (typeof depense.rubriques === "object" && depense.rubriques !== null) rubrique = depense.rubriques.nom || depense.rubriques.label || "-";
      
      if (!result[chef]) result[chef] = {};
      if (!result[chef][rubrique]) result[chef][rubrique] = [];
      result[chef][rubrique].push(depense);
    });
    return result;
  };

  const grouped = groupByChantierRubrique(depensesFiltrees);

  const groupDepensesByRubrique = (depenses) => {
    const grouped = {};
    depenses.forEach(dep => {
      let rubrique = "-";
      if (Array.isArray(dep.rubriques) && dep.rubriques[0]?.rubrique) rubrique = dep.rubriques[0].rubrique;
      else if (typeof dep.rubriques === "object" && dep.rubriques !== null && dep.rubriques.rubrique) rubrique = dep.rubriques.rubrique;
      else if (typeof dep.rubriques === "string") rubrique = dep.rubriques;
      
      if (!grouped[rubrique]) grouped[rubrique] = [];
      grouped[rubrique].push(dep);
    });
    return grouped;
  };

  const handleReset = () => {
    setFiltreDateDebut('');
    setFiltreDateFin('');
    setShowResults(false);
  };

  const handleSearch = () => {
    setShowResults(true);
  };

  const handleExportProject = (projectData) => {
    setSelectedProjectData(projectData);
    setShowExportModal(true);
  };

  const handleExport = (format) => {
    if (!selectedProjectData) return;
    
    const exportData = {
      ...selectedProjectData,
      filtreDateDebut,
      filtreDateFin
    };
    
    switch (format) {
      case 'excel':
        exportToExcelCharges(exportData);
        break;
      case 'pdf':
        exportToPDF(exportData);
        break;
      case 'svg':
        exportToSVG(exportData);
        break;
      default:
        exportToExcelCharges(exportData);
    }
    
    setShowExportModal(false);
  };

  return (
    <>
      <div className="space-y-8">
        <div className="bg-white rounded-xl shadow-lg border-0 shadow-gray-200">
          <div className="border-b border-gray-100 px-8 py-6">
            <h2 className="text-xl font-bold text-gray-800">État des dépenses validées DG</h2>
            <p className="text-gray-500 mt-1">Filtrez les dépenses par période</p>
          </div>
          
          {/* Affichage des erreurs */}
          {error && (
            <div className="px-8 py-4 bg-red-50 border-l-4 border-red-500">
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          {/* Filtres */}
          <div className="px-8 py-6 bg-gray-50 border-b border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de début
                </label>
                <input
                  type="date"
                  value={filtreDateDebut}
                  onChange={(e) => setFiltreDateDebut(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de fin
                </label>
                <input
                  type="date"
                  value={filtreDateFin}
                  onChange={(e) => setFiltreDateFin(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                />
              </div>
            </div>
            
            {/* Information sur le filtrage */}
            {(filtreDateDebut && filtreDateFin) && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  {depensesFiltrees.length} dépense(s) trouvée(s) pour la période du {new Date(filtreDateDebut).toLocaleDateString('fr-FR')} au {new Date(filtreDateFin).toLocaleDateString('fr-FR')}
                </p>
              </div>
            )}
            
            {/* Message si une seule date est renseignée */}
            {(filtreDateDebut && !filtreDateFin) || (!filtreDateDebut && filtreDateFin) ? (
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-700">
                  ⚠️ Veuillez renseigner les deux dates (début et fin) pour effectuer le filtrage
                </p>
              </div>
            ) : null}
            
            {/* Boutons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleReset}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
              >
                <span>Réinitialiser</span>
              </button>
              <button
                onClick={handleSearch}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                disabled={loading || !filtreDateDebut || !filtreDateFin}
                style={{
                  opacity: (!filtreDateDebut || !filtreDateFin) ? 0.5 : 1,
                  cursor: (!filtreDateDebut || !filtreDateFin) ? "not-allowed" : "pointer"
                }}
              >
                <span>Filtrer</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Tableau des résultats */}
          {showResults && filtreDateDebut && filtreDateFin && (
            <div className="p-8">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-gray-600">Chargement des données...</span>
                </div>
              ) : depensesFiltrees.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {allDepenses.length === 0 ? 
                    "Aucune dépense validée DG trouvée dans la base de données." :
                    "Aucune dépense trouvée pour la période sélectionnée."
                  }
                  <div className="mt-2 text-sm text-gray-400">
                    Période : {new Date(filtreDateDebut).toLocaleDateString('fr-FR')} - {new Date(filtreDateFin).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50 border-y border-gray-100">
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider rounded-l-lg">
                          Rubrique
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Sous-rubrique
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Fournisseur
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Montant
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider rounded-r-lg">
                          Date validation
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(grouped).map(([chef, rubriques], chefIdx) => {
                        const firstDep = Object.values(rubriques)[0]?.[0];
                        const projet = firstDep?.nomProjet || firstDep?.projet || "-";
                        const chefChantier = firstDep?.chefChantier || chef || "-";

                        const rubriqueTotals = {};
                        Object.entries(groupDepensesByRubrique(Object.values(rubriques).flat())).forEach(([rubrique, deps]) => {
                          rubriqueTotals[rubrique] = deps.reduce((sum, dep) => sum + Number(dep.montant || 0), 0);
                        });

                        const totalProjet = Object.values(rubriqueTotals).reduce((a, b) => a + b, 0);
                        const depensesProjet = Object.values(rubriques).flat();

                        return (
                          <React.Fragment key={projet + chefChantier}>
                            <tr>
                              <td colSpan={8} className="pt-8">
                                <div className="rounded-xl shadow-lg bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 mb-2">
                                  <div className="flex items-center px-6 py-4">
                                    <span className="inline-block bg-blue-600 text-white rounded-full px-4 py-2 font-bold text-lg shadow">
                                      {projet}
                                    </span>
                                    <span className="ml-4 text-blue-900 font-semibold text-base">
                                      Chef de chantier : {chefChantier}
                                    </span>
                                    <span className="ml-auto text-blue-900 font-bold text-base mr-4">
                                      Total projet : {totalProjet.toLocaleString()} FCFA
                                    </span>
                                    {/* Bouton Extraire */}
                                    <button
                                      onClick={() => handleExportProject({
                                        projet,
                                        chefChantier,
                                        depenses: depensesProjet,
                                        total: totalProjet
                                      })}
                                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 shadow"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <span>Extraire</span>
                                    </button>
                                  </div>
                                  <table className="min-w-full bg-transparent">
                                    <tbody>
                                      {Object.entries(groupDepensesByRubrique(Object.values(rubriques).flat())).map(([rubrique, deps]) => (
                                        <React.Fragment key={rubrique}>
                                          <tr>
                                            <td colSpan={8} className="py-0">
                                              <div
                                                className="flex items-center gap-3 px-6 py-3 my-2 rounded-lg shadow-md"
                                                style={{
                                                  background: "linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)",
                                                  boxShadow: "0 4px 16px 0 #2563eb22"
                                                }}
                                              >
                                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/30 mr-2">
                                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2a4 4 0 014-4h4M7 7h.01M7 11h.01M7 15h.01" />
                                                  </svg>
                                                </span>
                                                <span className="font-bold text-white text-lg tracking-wide drop-shadow">{rubrique}</span>
                                                <span className="ml-auto font-bold text-white text-base drop-shadow">
                                                  {rubriqueTotals[rubrique].toLocaleString()} FCFA
                                                </span>
                                              </div>
                                            </td>
                                          </tr>
                                          {deps.map((dep, idx) => (
                                            <tr key={dep.id} className={`transition ${idx % 2 === 0 ? "bg-blue-50/60" : "bg-white"}`}>
                                              <td className="px-6 py-3"></td>
                                              <td className="px-6 py-3 text-blue-900 font-semibold">
                                                {Array.isArray(dep.rubriques) && dep.rubriques[0]?.sousRubrique
                                                  ? dep.rubriques[0].sousRubrique
                                                  : (typeof dep.rubriques === "object" && dep.rubriques !== null && dep.rubriques.sousRubrique
                                                      ? dep.rubriques.sousRubrique
                                                      : "-")}
                                              </td>
                                              <td className="px-6 py-3 text-gray-700">
                                                {Array.isArray(dep.rubriques) && dep.rubriques[0]?.fournisseur
                                                  ? dep.rubriques[0].fournisseur
                                                  : dep.fournisseur || "-"}
                                              </td>
                                              <td className="px-6 py-3 text-gray-700">
                                                {dep.description
                                                  ? (
                                                    <button
                                                      className="text-blue-600 underline font-medium"
                                                      onClick={() => {
                                                        setSelectedDescription(dep.description);
                                                        setShowDescriptionModal(true);
                                                      }}
                                                    >
                                                      Voir
                                                    </button>
                                                  )
                                                  : <span className="text-gray-400">-</span>
                                                }
                                              </td>
                                              <td className="px-6 py-3 text-right font-bold text-blue-900">
                                                {Number(dep.montant).toLocaleString()} <span className="text-xs font-normal text-gray-500">FCFA</span>
                                              </td>
                                              <td className="px-6 py-3 text-center text-gray-600">
                                                {(dep.dateValidation || dep.dateValidationDG || dep.created)
                                                  ? (() => {
                                                      const dateField = dep.dateValidation || dep.dateValidationDG || dep.created;
                                                      const date = dateField.seconds 
                                                        ? new Date(dateField.seconds * 1000)
                                                        : new Date(dateField);
                                                      return date.toLocaleDateString('fr-FR', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                      });
                                                    })()
                                                  : "-"}
                                              </td>
                                            </tr>
                                          ))}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-200">
                        <td colSpan="5" className="px-6 py-4 text-sm font-bold text-gray-700">
                          Total
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-gray-900">
                          {calculateTotal().toLocaleString()} FCFA
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Modal description */}
      {showDescriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Description détaillée</h3>
              <button
                onClick={() => setShowDescriptionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="text-xl">✗</span>
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 whitespace-pre-line">{selectedDescription}</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowDescriptionModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        projectName={selectedProjectData?.projet}
      />
    </>
  );
}
